import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Package,
  Truck,
  Heart,
  MapPin,
  Ticket,
  Wallet,
  ChevronRight,
  ShoppingBag,
  Star,
  Gift,
  Bell,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  Settings,
  Headphones,
  ArrowUpRight,
  Sparkles,
  Zap,
  ShieldCheck,
  Calendar,
  FileText
} from 'lucide-react';
import { authService } from '../api/auth';
import { useAuth } from '../context/AuthContext';

function resolveApiUrl(): string {
  const fromEnv = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
    return '/api';
  }
  return 'http://localhost:5001/api';
}

const API_URL = resolveApiUrl();

interface Order {
  _id: string;
  orderNumber: string;
  items: Array<{
    _id?: string;
    name?: string;
    image?: string;
    product?: {
      name: string;
      images: Array<{ url: string }>;
    };
    quantity: number;
    price: {
      selling: number;
      mrp?: number;
    };
  }>;
  totalAmount: number;
  status: string;
  createdAt: string;
  paymentStatus: string;
  totals?: {
    subtotal?: number;
    discount?: number;
    shipping?: number;
    tax?: number;
    grandTotal: number;
  };
}

interface Coupon {
  _id: string;
  code: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  discountValue: number;
  validUntil: string;
}

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user: authUser, isAuthenticated, isLoading: authLoading, logout } = useAuth();
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState({
    totalOrders: 0,
    activeOrders: 0,
    deliveredOrders: 0,
    wishlistCount: 0,
    savedAddresses: 0,
    rewardPoints: 0,
    totalSpent: 0,
    couponsCount: 0
  });
  const [recentOrders, setRecentOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [coupons, setCoupons] = useState<Coupon[]>([]);

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) {
      return;
    }

    // ProtectedRoute already handles authentication check
    // Just load user data if authenticated
    if (isAuthenticated && authUser) {
      setUser(authUser);
      fetchDashboardData();
    } else if (isAuthenticated) {
      // If authenticated but no authUser, try localStorage
      const currentUser = authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        fetchDashboardData();
      }
    }
  }, [isAuthenticated, authUser, authLoading]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Fetch user data with stats
      const userResponse = await fetch(`${API_URL}/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userResponse.status === 401) {
        await logout();
        navigate('/login');
        return;
      }
      
      const userData = await userResponse.json();
      
      if (userData.success) {
        setUser(userData.data);
        setStats(prev => ({
          ...prev,
          totalOrders: userData.data.stats?.totalOrders || 0,
          wishlistCount: userData.data.stats?.wishlistCount || 0,
          savedAddresses: userData.data.stats?.addressesCount || 0,
          totalSpent: userData.data.stats?.totalSpent || 0,
          rewardPoints: userData.data.rewardPoints || 0,
        }));
      }
      
      // Fetch recent orders
      const ordersResponse = await fetch(`${API_URL}/orders/my-orders?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (ordersResponse.status === 401) {
        await logout();
        navigate('/login');
        return;
      }
      
      const ordersData = await ordersResponse.json();
      
      if (ordersData.success) {
        setRecentOrders(ordersData.data || []);
        
        const orders = ordersData.data || [];
        const active = orders.filter((o: Order) => ['pending', 'processing', 'shipped'].includes(o.status)).length;
        const delivered = orders.filter((o: Order) => o.status === 'delivered').length;
        
        setStats(prev => ({
          ...prev,
          activeOrders: active,
          deliveredOrders: delivered
        }));
      }
      
      // Fetch coupons
      const couponsResponse = await fetch(`${API_URL}/coupons/active`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const couponsData = await couponsResponse.json();
      
      if (couponsData.success) {
        setCoupons(couponsData.data || []);
        setStats(prev => ({ ...prev, couponsCount: couponsData.data?.length || 0 }));
      }
      
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-800 border-amber-200',
      processing: 'bg-blue-100 text-blue-800 border-blue-200',
      shipped: 'bg-purple-100 text-purple-800 border-purple-200',
      delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-3.5 h-3.5" />;
      case 'processing': return <Zap className="w-3.5 h-3.5" />;
      case 'shipped': return <Truck className="w-3.5 h-3.5" />;
      case 'delivered': return <CheckCircle className="w-3.5 h-3.5" />;
      default: return <AlertCircle className="w-3.5 h-3.5" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-200 border-t-indigo-600" />
        <p className="text-slate-600 text-sm font-medium">Loading your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6">
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 rounded-2xl p-6 sm:p-8 text-white shadow-lg shadow-purple-200/40">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <span className="text-sm font-medium text-white/90">Welcome back</span>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-1">
                {user?.firstName ? `Hello, ${user.firstName}!` : 'Hello!'}
              </h2>
              <p className="text-white/80 text-sm sm:text-base">
                Here's what's happening with your account today.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-4 py-3">
                <p className="text-xs text-white/80">Member Since</p>
                <p className="text-sm font-semibold">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }) : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Link to="/orders" className="group bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                <ShoppingBag className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-1 rounded-full">
                {stats.activeOrders} Active
              </span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.totalOrders}</p>
            <p className="text-sm text-gray-500 mt-1">Total Orders</p>
          </Link>

          <Link to="/orders?status=delivered" className="group bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-emerald-200 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-emerald-50 rounded-lg group-hover:bg-emerald-100 transition-colors">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-emerald-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.deliveredOrders}</p>
            <p className="text-sm text-gray-500 mt-1">Delivered</p>
          </Link>

          <Link to="/wishlist" className="group bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-pink-200 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-pink-50 rounded-lg group-hover:bg-pink-100 transition-colors">
                <Heart className="w-5 h-5 text-pink-600" />
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-pink-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.wishlistCount}</p>
            <p className="text-sm text-gray-500 mt-1">Wishlist Items</p>
          </Link>

          <Link to="/addresses" className="group bg-white rounded-xl p-4 sm:p-6 shadow-sm border border-gray-100 hover:shadow-md hover:border-amber-200 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="p-2.5 bg-amber-50 rounded-lg group-hover:bg-amber-100 transition-colors">
                <MapPin className="w-5 h-5 text-amber-600" />
              </div>
              <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-amber-500" />
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-gray-900">{stats.savedAddresses}</p>
            <p className="text-sm text-gray-500 mt-1">Saved Addresses</p>
          </Link>
        </div>

        {/* Rewards & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Reward Points Card */}
          <div className="lg:col-span-1 bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 text-white shadow-lg shadow-orange-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Star className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-sm text-white/90">Reward Points</p>
                  <p className="text-3xl font-bold">{stats.rewardPoints}</p>
                </div>
              </div>
              <Gift className="w-12 h-12 text-white/20" />
            </div>
            <Link
              to="/coupons"
              className="flex items-center justify-center gap-2 w-full py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-sm font-medium transition-colors"
            >
              View Coupons <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Coupons */}
          <div className="lg:col-span-1 space-y-4">

            <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-rose-50 rounded-lg">
                  <Ticket className="w-5 h-5 text-rose-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Active Coupons</p>
                  <p className="text-xl font-bold text-gray-900">{stats.couponsCount}</p>
                </div>
                <Bell className="w-5 h-5 text-rose-400" />
              </div>
              <Link
                to="/coupons"
                className="text-sm font-medium text-rose-600 hover:text-rose-700 flex items-center gap-1"
              >
                View Coupons <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="lg:col-span-1 bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <Link
                to="/orders"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors group"
              >
                <Truck className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">Track Orders</span>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link
                to="/my-bills"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-blue-50 transition-colors group"
              >
                <FileText className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-blue-700">My Bills</span>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link
                to="/addresses"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-emerald-50 transition-colors group"
              >
                <MapPin className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-emerald-700">Manage Addresses</span>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link
                to="/wishlist"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-pink-50 transition-colors group"
              >
                <Heart className="w-5 h-5 text-pink-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-pink-700">My Wishlist</span>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link
                to="/profile"
                className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-purple-50 transition-colors group"
              >
                <Settings className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700 group-hover:text-purple-700">Account Settings</span>
                <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
            </div>
          </div>
        </div>

        {/* Recent Orders Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-5 sm:p-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Recent Orders</h3>
                <p className="text-sm text-gray-500">Track and manage your purchases</p>
              </div>
            </div>
            <Link
              to="/orders"
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              View All Orders
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          {recentOrders.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-10 h-10 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">No orders yet</h4>
              <p className="text-gray-500 mb-6 max-w-sm mx-auto">Start shopping to see your orders here and track their status.</p>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors"
              >
                <ShoppingBag className="w-5 h-5" />
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentOrders.slice(0, 3).map((order) => (
                <div key={order._id} className="p-4 sm:p-6 hover:bg-gray-50/50 transition-colors">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={order.items[0]?.image || order.items[0]?.product?.images?.[0]?.url || '/placeholder-product.png'}
                          alt={order.items[0]?.name || order.items[0]?.product?.name || 'Product'}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-gray-900 truncate">
                          {order.items[0]?.name || order.items[0]?.product?.name || 'Unknown Product'}
                          {order.items.length > 1 && (
                            <span className="text-gray-500 font-normal"> +{order.items.length - 1} more</span>
                          )}
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5">
                          <span className="text-sm text-gray-500">Order #{order.orderNumber}</span>
                          <span className="text-gray-300">&middot;</span>
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(order.createdAt).toLocaleDateString('en-IN')}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            <span className="capitalize">{order.status}</span>
                          </span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(order.totals?.grandTotal || order.totalAmount || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/orders/${order._id}`}
                      className="inline-flex items-center justify-center gap-1 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition-colors sm:self-center"
                    >
                      View Details
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { Icon: ShieldCheck, title: 'Secure checkout', sub: 'Encrypted payments', bg: 'bg-emerald-50', color: 'text-emerald-600' },
            { Icon: Truck, title: 'Fast delivery', sub: 'Track orders', bg: 'bg-blue-50', color: 'text-blue-600' },
            { Icon: Package, title: 'Genuine products', sub: 'Quality checked', bg: 'bg-violet-50', color: 'text-violet-600' },
            { Icon: Headphones, title: 'Support', sub: 'Help when you need it', bg: 'bg-amber-50', color: 'text-amber-600' },
          ].map(({ Icon, title, sub, bg, color }) => (
            <div key={title} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm border border-slate-100">
              <div className={`p-2 rounded-lg shrink-0 ${bg}`}>
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{title}</p>
                <p className="text-xs text-slate-500 mt-0.5">{sub}</p>
              </div>
            </div>
          ))}
        </div>
    </div>
  );
};

export default UserDashboard;


